declare global {
  interface Window {
    gtag: any
  }
}

import { GA4_MEASUREMENT_ID, GA4_STREAM_ID, GOOGLE_ADS_CONVERSION_ID } from './ga4'

export const GA_TRACKING_ID = GA4_MEASUREMENT_ID

/**
 * Sets the default consent state for Google Analytics.
 * Should be called before any pageview or event tracking.
 */
export const setDefaultConsentState = (region?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Default to denied for all consent types
    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      wait_for_update: 500,
    })

    // Enable URL passthrough
    window.gtag('set', 'url_passthrough', true)
  }
}

/**
 * Updates the consent state based on user choices.
 */
export const updateConsentState = ({
  adStorage = 'denied',
  adUserData = 'denied',
  adPersonalization = 'denied',
  analyticsStorage = 'denied',
}: {
  adStorage?: 'granted' | 'denied'
  adUserData?: 'granted' | 'denied'
  adPersonalization?: 'granted' | 'denied'
  analyticsStorage?: 'granted' | 'denied'
}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      ad_storage: adStorage,
      ad_user_data: adUserData,
      ad_personalization: adPersonalization,
      analytics_storage: analyticsStorage,
    })
  }
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/pages
/**
 * Captures a pageview in Google Analytics.
 */
export const pageview = (url: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Extract only the path portion if a full URL is provided
    let path = url
    try {
      // If this is a full URL, extract just the pathname
      if (url.startsWith('http')) {
        const urlObj = new URL(url)
        path = urlObj.pathname + urlObj.search + urlObj.hash
      } else if (url.startsWith('//')) {
        // Handle protocol-relative URLs
        path = url.substring(url.indexOf('/', 2))
      } else if (url.startsWith('/')) {
        // Already a path, use as is
        path = url
      }
    } catch (e) {
      console.error('Error parsing URL in pageview tracking:', e)
    }

    // Use 'event' for SPA pageviews instead of 'config'
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: path,
      send_to: GA_TRACKING_ID,
      stream_id: GA4_STREAM_ID,
    })
  }
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/events
/**
 * Captures a custom event in Google Analytics.
 */
export const event = ({
  action,
  category,
  label,
  value,
}: {
  action: string
  category: string
  label: string
  value: number
}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      stream_id: GA4_STREAM_ID,
    })
  }
}

/**
 * Tracks a Google Ads conversion
 */
export const trackConversion = ({
  conversionId = GOOGLE_ADS_CONVERSION_ID,
  conversionLabel,
  value = 0,
  currency = 'EUR',
  transactionId = '',
}: {
  conversionId?: string
  conversionLabel: string
  value?: number
  currency?: string
  transactionId?: string
}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: `${conversionId}/${conversionLabel}`,
      value: value,
      currency: currency,
      transaction_id: transactionId,
    })
    console.log(`Tracked Google Ads conversion: ${conversionLabel}`)
  }
}
